import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { ThemeProvider } from './hooks/useTheme';
import Layout from './components/dashboard/Layout';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import ClientsPage from './pages/ClientsPage';
import ClientProfilePage from './pages/ClientProfilePage';
import ProjectsPage from './pages/ProjectsPage';
import DeliverablesPage from './pages/DeliverablesPage';
import SubscriptionsPage from './pages/SubscriptionsPage';
import InvoicesPage from './pages/InvoicesPage';
import ExpensesPage from './pages/ExpensesPage';
import ReportsPage from './pages/ReportsPage';
import SettingsPage from './pages/SettingsPage';
import TreasuryPage from './pages/TreasuryPage';
import PackagesPage from './pages/PackagesPage';
import EmployeesPage from './pages/EmployeesPage';
import CommissionsPage from './pages/CommissionsPage';
import ClientTrackingPage from './pages/ClientTrackingPage';
import EmployeeTrackingPage from './pages/EmployeeTrackingPage';
import TeamPerformancePage from './pages/TeamPerformancePage';
import TmDashboardPage from './pages/tm/TmDashboardPage';
import TmTasksPage from './pages/tm/TmTasksPage';
import TmTeamsPage from './pages/tm/TmTeamsPage';
import TmSupervisorsPage from './pages/tm/TmSupervisorsPage';
import TmEarningsPage from './pages/tm/TmEarningsPage';
import TmComplaintsPage from './pages/tm/TmComplaintsPage';
import TmLoginPage from './pages/tm/TmLoginPage';
import TmLayout from './components/tm/TmLayout';



function PrivateRoute({ children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (['supervisor', 'team_leader', 'team_member'].includes(user.role)) {
    return <Navigate to="/tm/tasks" replace />;
  }
  return children;
}

function TmPrivateRoute({ children }) {
  const { user } = useAuth();
  return user ? children : <Navigate to="/team-login" replace />;
}

function AppRoutes() {
  const { user } = useAuth();
  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <LoginPage />} />
      <Route path="/team-login" element={user ? <Navigate to="/tm/tasks" replace /> : <TmLoginPage />} />
      <Route path="/track/:token" element={<ClientTrackingPage />} />
      <Route path="/employee-track/:token" element={<EmployeeTrackingPage />} />
      <Route path="/" element={<PrivateRoute><Layout /></PrivateRoute>}>
        <Route index element={user?.role === 'employee' ? <Navigate to="/tm/tasks" replace /> : <DashboardPage />} />
        <Route path="clients" element={user?.role === 'employee' ? <Navigate to="/tm/tasks" replace /> : <ClientsPage />} />
        <Route path="clients/:id" element={<ClientProfilePage />} />
        <Route path="employees" element={<EmployeesPage />} />
        <Route path="team" element={<TeamPerformancePage />} />
        <Route path="projects" element={<ProjectsPage />} />
        <Route path="subscriptions" element={<SubscriptionsPage />} />
        <Route path="deliverables" element={<DeliverablesPage />} />
        <Route path="invoices" element={<InvoicesPage />} />
        <Route path="expenses" element={<ExpensesPage />} />
        <Route path="treasury" element={<TreasuryPage />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="commissions" element={<CommissionsPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="packages" element={<PackagesPage />} />
      </Route>
      {/* Task Manager V2 — Separate Layout */}
      <Route path="/tm" element={<TmPrivateRoute><TmLayout /></TmPrivateRoute>}>
        <Route index element={<Navigate to="/tm/tasks" replace />} />
        <Route path="tasks" element={<TmTasksPage />} />
        <Route path="teams" element={<TmTeamsPage />} />
        <Route path="supervisors" element={<TmSupervisorsPage />} />
        <Route path="earnings" element={<TmEarningsPage />} />
        <Route path="dashboard" element={<TmDashboardPage />} />
        <Route path="complaints" element={<TmComplaintsPage />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-center" toastOptions={{
            duration: 3000,
            style: { fontFamily: 'Tajawal, sans-serif', direction: 'rtl' }
          }} />
        </BrowserRouter>
      </ThemeProvider>
    </AuthProvider>
  );
}
